import {
  HeartPulse, ShoppingBag, Anchor, HandHeart, Heart, Leaf,
  CreditCard, FileCheck, Users, Clock, TrendingUp, Package,
  Truck, ShieldCheck, BarChart3, Brain, Zap, RefreshCw,
  Globe, Database, Award, Building2, Target, Briefcase,
  Factory, Lock, Sparkles, Layers
} from "lucide-react";

const industries = {
  ltc: {
    slug: "ltc",
    color: "#E95D54",
    heroBg: "bg-[#465155]",
    ctaBg: "bg-[#465155]",
    eyebrow: "Long-Term Care & Geriatric Care Solutions",
    customers: [
      { name: "Dover Park Hospice", logo: "/assets/customers/dover-park-hospice.png" },
      { name: "St Lukes Eldercare", logo: "/assets/customers/st-lukes-eldercare.png" },
      { name: "Home Nursing Foundation", logo: "/assets/customers/home-nursing-foundation.jpg" },
      { name: "United Medicare Centre", logo: "/assets/customers/united-medicare-centre.png" },
      { name: "NTUC Health", logo: "/assets/customers/ntuc-health.jpg" },
      { name: "Lentor Health", logo: "/assets/customers/lentor-health.png" },
      { name: "APEX Harmony Lodge", logo: "/assets/customers/apex-harmony-lodge.jpg" },
      { name: "Man Fut Tong Nursing Home", logo: "/assets/customers/man-fut-tong.jpg" },
      { name: "HCA Hospice Care", logo: "/assets/customers/hca-hospice-care.jpg" },
    ],
    headline: "ERP, Patient Accounting, Analytics & AI for Healthcare Providers",
    subheadline: "Singapore's ageing population is accelerating demand for nursing homes, ILTC facilities, home care providers, and geriatric care institutions. Asterisk delivers integrated solutions purpose-built for this sector.",
    stats: [
      { value: "26+", label: "Nursing Homes Served", icon: Building2 },
      { value: "24+", label: "Years Microsoft ERP", icon: Clock },
      { value: "MOH", label: "Compliant Billing", icon: ShieldCheck },
      { value: "CareOps", label: "365 Platform", icon: HeartPulse },
    ],
    sections: [
      {
        title: "Finance & Patient Accounting Excellence",
        description: "We help LTC providers modernise financial operations with CareOps 365 — creating a seamless bridge between care delivery and financial management.",
        items: ["Patient billing automation", "RAF & Means Test matrix calculations", "Subsidy sequencing and grant management", "MOH-compliant invoicing", "ILTC submission integration", "Cash flow forecasting", "Multi-period GST management"],
        businessValue: ["Reduced billing errors", "Faster claims submission", "Improved cash collection cycles", "Stronger audit readiness"],
      },
      {
        title: "Operational & ERP Integration",
        description: "Microsoft-based ERP systems for full operational visibility — from ward-level activity to financial performance.",
        items: ["Finance & Accounting", "Procurement & Inventory", "Asset Management", "Donor & Grant Tracking", "Multi-site consolidation", "HR & Payroll integration"],
      },
      {
        title: "Analytics & AI for Healthcare",
        description: "Transform healthcare data into strategic insight — responsibly and securely.",
        items: ["Subsidy utilisation analytics", "Revenue leakage detection", "Occupancy forecasting", "Cost-to-serve analysis", "Predictive financial modelling", "AI-enabled document automation"],
      },
    ],
    whyTrust: ["24+ years Microsoft ERP expertise", "Proven experience across 26 nursing homes", "MOH-compliant billing architecture", "Deep domain knowledge in Singapore's ILTC landscape"],
    ctaTitle: "Let's Modernise Your Care Operations",
    ctaPath: "Long-Term Care & Geriatric Care",
  },
  retail: {
    slug: "retail",
    color: "#308795",
    heroBg: "bg-[#1a3a42]",
    ctaBg: "bg-[#1a3a42]",
    eyebrow: "Retail & Distribution Solutions",
    customers: [
      { name: "SKP", logo: "/assets/customers/skp.png" },
      { name: "Snorre Food", logo: "/assets/customers/snorrefood.png" },
      { name: "Crystal Wines", logo: "/assets/customers/crystalwines.png" },
      { name: "Eastern Harvest", logo: "/assets/customers/eastern-harvest.png" },
      { name: "SIS", logo: "/assets/customers/sis.png" },
    ],
    headline: "ERP, CRM, Supply Chain Intelligence & AI for Modern Commerce",
    subheadline: "Retailers and distributors operate in a high-pressure environment — tight margins, demand volatility, multi-channel sales, inventory complexity, and credit risk exposure.",
    stats: [
      { value: "180+", label: "Projects Deployed", icon: Layers },
      { value: "15", label: "Industries Served", icon: Building2 },
      { value: "SKU", label: "Level Margin Visibility", icon: BarChart3 },
      { value: "AI", label: "Demand Prediction", icon: Brain },
    ],
    sections: [
      {
        title: "Finance & Margin Control",
        description: "ERP systems providing real-time profitability analysis and financial control.",
        items: ["Real-time profitability analysis", "SKU-level margin visibility", "Multi-warehouse inventory costing", "Credit control automation", "Consolidated financial reporting", "GST and regional tax compliance"],
        businessValue: ["Margin protection", "Reduced write-offs", "Faster month-end closing", "Improved working capital management"],
      },
      {
        title: "Supply Chain & Operations Optimisation",
        description: "AI-enhanced supply chain capabilities for demand prediction and reorder optimisation.",
        items: ["Inventory demand forecasting", "Purchase planning automation", "Multi-location warehouse management", "Batch & serial tracking", "Order fulfilment integration", "Vendor performance monitoring"],
      },
      {
        title: "CRM & Customer Intelligence",
        description: "Unify customer data across in-store, e-commerce, B2B sales, and distributors.",
        items: ["Sales pipeline tracking", "Customer lifetime value analysis", "Targeted promotions", "Loyalty analytics", "Dynamic pricing optimisation", "Churn prediction"],
      },
    ],
    whyTrust: ["Deep retail & distribution domain expertise", "Microsoft Dynamics 365 specialisation", "Regional deployment across ASEAN", "AI-powered inventory and demand intelligence"],
    ctaTitle: "Scale Your Retail & Distribution Operations",
    ctaPath: "Retail & Distribution",
  },
  npo: {
    slug: "npo",
    color: "#7EA13E",
    heroBg: "bg-[#273519]",
    ctaBg: "bg-[#273519]",
    eyebrow: "Not-for-Profit Organisations (NPO) & Charities",
    customers: [
      { name: "Dover Park Hospice", logo: "/assets/customers/dover-park-hospice.png" },
      { name: "Home Nursing Foundation", logo: "/assets/customers/home-nursing-foundation.jpg" },
      { name: "Man Fut Tong Nursing Home", logo: "/assets/customers/man-fut-tong.jpg" },
      { name: "St Lukes Eldercare", logo: "/assets/customers/st-lukes-eldercare.png" },
      { name: "YWCA Singapore", logo: "/assets/customers/ywca.png" },
    ],
    headline: "Financial Transparency, Grant Management & Digital Governance",
    subheadline: "Charities and non-profit organisations must balance grant compliance, donor transparency, programme accountability, financial governance, and limited administrative resources.",
    stats: [
      { value: "16+", label: "NPOs Supported", icon: HandHeart },
      { value: "20+", label: "Years NPO Experience", icon: Clock },
      { value: "Fund", label: "Accounting Expertise", icon: CreditCard },
      { value: "Audit", label: "Ready Reporting", icon: FileCheck },
    ],
    sections: [
      {
        title: "Finance & Grant Management",
        description: "ERP and finance systems for multiple non-profit organisations with strong governance controls.",
        items: ["Fund accounting", "Restricted & unrestricted grants", "Donation tracking", "Multi-programme budgeting", "Audit-ready reporting", "Regulatory compliance"],
        businessValue: ["Strong governance controls", "Transparent reporting to donors", "Efficient grant utilisation", "Reduced manual reconciliation"],
      },
      {
        title: "Operations & Programme Visibility",
        description: "Ensure accountability across programmes and stakeholders.",
        items: ["Procurement", "Asset management", "Multi-entity consolidation", "Donor CRM systems"],
      },
      {
        title: "Analytics & Impact Measurement",
        description: "Dashboards and AI tools for programme cost analysis and impact measurement.",
        items: ["Programme cost analysis", "Grant utilisation tracking", "Beneficiary reporting", "Financial sustainability modelling", "Document automation", "Predictive donation modelling"],
      },
    ],
    whyTrust: ["20+ years serving NPOs and charities", "Deep fund accounting expertise", "Regulatory compliance architecture", "Transparent governance frameworks"],
    ctaTitle: "Strengthen Your NPO's Financial Governance",
    ctaPath: "Not-for-Profit Organisations",
  },
  fnb: {
    slug: "fnb",
    color: "#E5BD77",
    heroBg: "bg-[#494546]",
    ctaBg: "bg-[#494546]",
    eyebrow: "Food, Beverage & Hospitality Solutions",
    customers: [
      { name: "Brewerkz", logo: "/assets/customers/brewerkz.png" },
      { name: "Grand Park Kodhipparu", logo: "/assets/customers/grandpark.png" },
      { name: "JM Ice", logo: "/assets/customers/jmice.png" },
      { name: "Neo Group", logo: "/assets/customers/neogroup.png" },
      { name: "Park Hotel Group", logo: "/assets/customers/parkhotel.png" },
    ],
    headline: "ERP, Supply Chain Intelligence & AI for Operational Efficiency",
    subheadline: "Food and hospitality operators face thin margins, demand volatility, perishable inventory, labour cost pressure, and compliance requirements.",
    stats: [
      { value: "Recipe", label: "Costing Analysis", icon: CreditCard },
      { value: "Multi", label: "Outlet Consolidation", icon: Building2 },
      { value: "AI", label: "Quote Automation", icon: Zap },
      { value: "Batch", label: "Control & Tracking", icon: Package },
    ],
    sections: [
      {
        title: "Finance & Cost Control",
        description: "ERP solutions for food cost optimisation and multi-outlet financial consolidation.",
        items: ["Recipe costing analysis", "Supplier price tracking", "Multi-outlet financial consolidation", "Cash flow forecasting", "GST compliance"],
        businessValue: ["Food cost optimisation", "Reduced wastage", "Accurate margin tracking", "Improved financial planning"],
      },
      {
        title: "Operations & Supply Chain",
        description: "Inventory management with AI accelerators that reduce quote turnaround from hours to seconds.",
        items: ["Inventory expiry tracking", "Batch control", "Purchase automation", "Demand planning", "Catering quotation automation"],
      },
      {
        title: "Analytics & Forecasting",
        description: "Dashboards for performance benchmarking, revenue forecasting, and waste reduction.",
        items: ["Outlet performance benchmarking", "Revenue forecasting", "Customer trend analysis", "Waste reduction insights"],
      },
    ],
    whyTrust: ["Deep F&B and hospitality domain expertise", "AI-powered catering quote automation", "Multi-outlet ERP consolidation", "Perishable inventory management solutions"],
    ctaTitle: "Optimise Your F&B Operations",
    ctaPath: "Food, Beverage & Hospitality",
  },
  marine: {
    slug: "marine",
    color: "#308795",
    heroBg: "bg-[#1a3a42]",
    ctaBg: "bg-[#1a3a42]",
    eyebrow: "Marine & Shipping Solutions",
    customers: [
      { name: "BHH Marine", logo: "/assets/customers/bhh.png" },
      { name: "BTS Tankers", logo: "/assets/customers/bts-tankers.png" },
      { name: "Nortrans", logo: "/assets/customers/nortrans.png" },
      { name: "Techmarine Hydraulic & Engineering", logo: "/assets/customers/techmarine.png" },
    ],
    headline: "ERP, Automation & AI for Maritime Operations",
    subheadline: "Marine and shipping operators manage complex ecosystems — international procurement, multi-currency transactions, logistics coordination, inventory movement, and compliance documentation.",
    stats: [
      { value: "Multi", label: "Currency Accounting", icon: Globe },
      { value: "Project", label: "Based Costing", icon: Briefcase },
      { value: "Doc AI", label: "Automation", icon: Brain },
      { value: "Route", label: "Optimisation", icon: Truck },
    ],
    sections: [
      {
        title: "Finance & Multi-Entity Control",
        description: "Multi-currency accounting with project-based costing and consolidated reporting.",
        items: ["Multi-currency accounting", "Project-based costing", "Consolidated financial reporting", "Credit risk management", "Cash flow forecasting"],
        businessValue: ["Improved financial visibility", "Faster reconciliation", "Reduced compliance risk", "Stronger margin control"],
      },
      {
        title: "Operations & Supply Chain Integration",
        description: "ERP systems managing inventory, warehouse, and logistics with Document AI automation.",
        items: ["Inventory tracking", "Warehouse operations", "Purchase order management", "Logistics integration", "Vendor coordination"],
      },
      {
        title: "AI & Operational Intelligence",
        description: "AI Design Factory solutions for maritime operational complexity.",
        items: ["Route optimisation models", "Demand forecasting", "Predictive maintenance insights", "Document automation engines", "PO extraction & invoice processing", "Shipping documentation capture"],
      },
    ],
    whyTrust: ["Maritime industry domain expertise", "Multi-currency and multi-entity ERP", "Document AI for shipping operations", "Regional deployment experience"],
    ctaTitle: "Modernise Your Maritime Operations",
    ctaPath: "Marine & Shipping",
  },
  esg: {
    slug: "esg",
    color: "#7EA13E",
    heroBg: "bg-[#273519]",
    ctaBg: "bg-[#273519]",
    eyebrow: "Sustainability & ESG Compliance Solutions",
    headline: "ESG Reporting, Carbon Accounting & Sustainable Supply Chain for SMEs",
    subheadline: "ESG is no longer optional — it's a supply chain requirement. Without structured ESG systems, SMEs risk losing contracts, failing compliance reviews, and facing reputational risk.",
    stats: [
      { value: "Scope", label: "1, 2 & 3 Emissions", icon: Globe },
      { value: "AI", label: "Powered ESG Data", icon: Brain },
      { value: "ERP", label: "Integrated ESG", icon: Database },
      { value: "SusTek", label: "Partnership", icon: Award },
    ],
    sections: [
      {
        title: "ESG Embedded Into ERP",
        description: "Using Microsoft Business Central Sustainability Management, ESG data sits alongside financial data — not outside it.",
        items: ["Carbon emission tracking", "Energy consumption data", "Waste reporting", "Water usage monitoring", "Supplier sustainability records"],
        businessValue: ["Single source of truth", "Reduced manual reporting effort", "Improved data accuracy", "Audit-ready documentation"],
      },
      {
        title: "SusTek AI-Powered ESG Data Manager",
        description: "AI-driven data consolidation and automated sustainability validation for enterprise-level intelligence without enterprise infrastructure.",
        items: ["Automated sustainability data validation", "Intelligent carbon calculation engines", "Emission factor libraries", "ESG dashboard visualisation", "Compliance-ready reporting formats"],
      },
      {
        title: "Sustainable Supply Chain Enablement",
        description: "Position your SME as a responsible, data-driven, future-ready supply chain partner.",
        items: ["Scope 3 emissions reporting", "Supplier carbon disclosures", "ESG performance benchmarking", "Sustainable procurement analytics", "Compliance document automation"],
      },
    ],
    whyTrust: ["Asterisk: 36+ years SME digital transformation", "SusTek: ESG domain & AI expertise", "Microsoft ERP integration capability", "Modular, scalable, cost-disciplined approach"],
    extraSection: {
      title: "Business Impact for SMEs",
      items: ["Retain key customers requiring ESG compliance", "Qualify for sustainable procurement programmes", "Improve tender success rates", "Respond confidently to ESG audits", "Reduce manual reporting workload", "Align sustainability with financial planning"],
    },
    ctaTitle: "Build a Sustainable Digital Foundation",
    ctaPath: "Sustainability & ESG",
  },
};

export default industries;
